import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import once from 'lodash/once';
import { bindActionCreators } from 'redux';

import {
	Chat,
	ChatFormButtonsComponents,
	ChatFormButtonsWrappers,
	ChatMessage,
	ChatMessageActionsComponent,
	ChatOtherComponents,
	ChatPlugins,
	ChatListHeaderSettingsType,
	ChatNameType,
	ChatMessagesHeaderSettingsType,
	ChatSettingsChatPageSettings,
	ChatStoreSettings,
	ChatSettingsFormSettings,
	PluginsDictionary,
	ChatSettingsRequests,
	ChatSettingsNotificationTypes,
} from '@common/react/components/Chat/Chat';
import { getAvatar } from '@common/react/utils/getAvatar';
import {
	getActionCreators,
	ApplicationStateWithChats,
	ChatActions,
} from '@common/react/components/Chat/Store/Chats';

import { BaseParams } from '@common/react/objects/BaseParams';
import { BaseUserWithAvatar } from '@common/typescript/objects/BaseUser';
import { getUserName } from '@common/react/utils/utils';
import { RegularMessagePlugin } from '@common/react/components/Chat/RegularMessagePlugin/RegularMessagePlugin';
import { FilePlugin } from '@common/react/components/Chat/FilesPlugin/FilesPlugin';
import { handleUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { RequestType } from '@common/react/components/RequestProvider/RequestProvider';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useMobileView } from '@common/react/hooks/useMobileView';

export interface ChatSettingsAvatarSettings<TUser extends BaseUserWithAvatar, TChat extends Chat<TUser>> {
	// maybe better return components with special interface
	getUserAvatar: (user: any) => React.ReactNode;
	getUserAvatarAtMention?: (user: any) => React.ReactNode;
	getChatAvatar: (chat: TChat, userId: number) => React.ReactNode;
	notificationAvatar: (state: any) => React.ReactNode;
	viewerAvatar?: (state: any) => React.ReactNode;
}

export interface ErrorHandlers {
	onChatsLoadError?: (err: string) => void;
	onChatMessagesLoadError?: (err: string) => void;
	onSaveMessageLoadError?: (err: string) => void;
	onChatMessageAccessError?: (err: string) => void;
	chatMessagesErrorComponent?: ({ reload }) => React.ReactNode;
}

type OnMessageDoubleClickDelegate<TChatMessage extends ChatMessage> = (
	e: React.MouseEvent<HTMLLIElement, MouseEvent>,
	message: TChatMessage,
	reply: (message: TChatMessage) => void,
) => void;

export interface ChatSettingsProviderProps<
	TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> {
	children: React.ReactNode | ((context: ChatSettingsProviderContext<TUser, TChatMessage, TChat>) => React.ReactNode);
	storageName?: string;
	requests?: ChatSettingsRequests;
	notificationTypes?: ChatSettingsNotificationTypes;
	avatarSettings?: ChatSettingsAvatarSettings<TUser, TChat>;
	formSettings?: ChatSettingsFormSettings;
	formButtons?: Array<ChatFormButtonsComponents>;
	formTags?: Array<ChatPlugins>;
	otherComponents?: Array<ChatOtherComponents>;
	pageSettings?: ChatSettingsChatPageSettings;
	chatFormButtonsWrappers?: ChatFormButtonsWrappers;
	chatStoreSettings?: Omit<ChatStoreSettings<TUser, TChatMessage, TChat>, 'getActionCreators'>
		& Partial<Pick<ChatStoreSettings<TUser, TChatMessage, TChat>, 'getActionCreators'>>;
	title?: string;
	withRemoteId?: boolean;
	chatsFilters?: BaseParams;
	chatListHeaderSettings?: ChatListHeaderSettingsType<TUser, TChat>;
	renderChatName?: ChatNameType<TUser, TChat>;
	messagesHeaderComponents?: ChatMessagesHeaderSettingsType<TUser, TChatMessage, TChat>;
	notificationHideDelay?: number;
	maxChatMessageNotificationCount?: number;
	messageActions?: Array<ChatMessageActionsComponent<TUser, TChatMessage>>;
	removeMessageConfirmation?: boolean;
	errorHandlers?: ErrorHandlers;
	emptyChatListMessage?: React.ReactNode | (({ filters, load }) => React.ReactNode);
	basePath?: string;
	messagesLoadMoreIndicator?: React.ReactNode;
	plugins?: PluginsDictionary;
	listComponent?: Array<ChatPlugins>;
	messageControl?: ChatPlugins;
	messageControlWrappers?: Array<ChatPlugins>;
	showActionsByHover?: boolean;
	showMessagesButtonInVideoModal?: boolean;
	openModalFromNotification?: boolean;
	openInModal?: boolean;
	onMessageClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>, message: TChatMessage) => void;
	onMessageDoubleClick?: OnMessageDoubleClickDelegate<TChatMessage>;
	messageAttachments?: Array<ChatPlugins>;
	messageAttachmentsBefore?: Array<ChatPlugins>;
	sendButtonWrapper?: ChatPlugins;
	getUser?: () => TUser;
	request?: RequestType;
	onNotificationClick?: (message: TChatMessage, defaultHandle: () => void) => void;
	handleAddChatByNewMessage?: (message: TChatMessage, chatFilters: BaseParams) => boolean;
	mobileViewWidth?: number;
}

export interface ChatSettingsProviderContextState<
	TUser extends BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> {
	requests: ChatSettingsRequests;
	plugins: PluginsDictionary;
	storageName: string;
	notificationTypes: ChatSettingsNotificationTypes;
	formSettings: ChatSettingsFormSettings;
	formButtons: Array<ChatFormButtonsComponents>;
	otherComponents: Array<ChatOtherComponents>;
	chatFormButtonsWrappers: ChatFormButtonsWrappers;
	pageSettings: ChatSettingsChatPageSettings;
	chatStoreSettings: ChatStoreSettings<TUser, TChatMessage, TChat>;
	title: string;
	avatarSettings: ChatSettingsAvatarSettings<TUser, TChat>;
	formTags: Array<ChatPlugins>;
	withRemoteId: boolean;
	chatListHeaderSettings: ChatListHeaderSettingsType<TUser, TChat>;
	messagesHeaderComponents: ChatMessagesHeaderSettingsType<TUser, TChatMessage, TChat>;
	notificationHideDelay: number;
	maxChatMessageNotificationCount: number;
	getUser: () => TUser;
	request: RequestType;
	messageActions?: Array<ChatMessageActionsComponent<TUser, TChatMessage>>;
	chatsFilters?: BaseParams;
	renderChatName?: ChatNameType<TUser, TChat>;
	removeMessageConfirmation?: boolean;
	errorHandlers?: ErrorHandlers;
	emptyChatListMessage?: React.ReactNode | (({ filters, load }) => React.ReactNode);
	basePath: string;
	messagesLoadMoreIndicator?: React.ReactNode;
	listComponent?: Array<ChatPlugins>;
	messageControl?: ChatPlugins;
	messageControlWrappers?: Array<ChatPlugins>;
	showActionsByHover?: boolean;
	showMessagesButtonInVideoModal?: boolean;
	modalMode?: boolean;
	openModalFromNotification?: boolean;
	onMessageClick?: (e: React.MouseEvent<HTMLLIElement, MouseEvent>, message: TChatMessage) => void;
	onMessageDoubleClick?: OnMessageDoubleClickDelegate<TChatMessage>;
	openInModal?: boolean;
	messageAttachments?: Array<ChatPlugins>;
	messageAttachmentsBefore?: Array<ChatPlugins>;
	sendButtonWrapper?: ChatPlugins;
	reloadChatId?: number;
	onNotificationClick?: (message: TChatMessage, defaultHandle: () => void) => void;
	handleAddChatByNewMessage?: (message: TChatMessage, chatFilters: BaseParams) => boolean;
	mobileViewWidth?: number;
	openedRef: { current: { value: boolean }};
}

type StartChatDelegate<TChat extends Chat = Chat> = (data: BaseParams, requestName?: string, openChat?: boolean) => Promise<TChat>;

export interface ChatSettingsProviderContextActions<TChat extends Chat> {
	setChatsFilters: React.Dispatch<React.SetStateAction<BaseParams>>;
	setModalMode: React.Dispatch<React.SetStateAction<boolean>>;
	startChat: StartChatDelegate<TChat>;
	setReloadChatId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

export interface ChatSettingsProviderContext<
	TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
> {
	state: ChatSettingsProviderContextState<TUser, TChatMessage, TChat>;
	actions: ChatSettingsProviderContextActions<TChat>;
}

export const getUserPick = <TChat extends Chat = Chat>(chat: TChat, userId: number | undefined): JSX.Element => {
	const users = chat.contacts.filter((user: BaseUserWithAvatar) => user.id !== userId);
	if (users.length === 1) {
		return <div className="chat-list__item-avatar" style={{ backgroundImage: `url(${getAvatar(users[0])})` }} title={getUserName(users[0])} />;
	}

	return <div className="chat-list__item-avatar-multiple">
		{users.slice(0, 4).map((user: BaseUserWithAvatar) =>
			<div
				className="chat-list__item-avatar chat-list__item-avatar_min"
				key={user.id}
				title={getUserName(user)}
				style={{ backgroundImage: `url(${getAvatar(user)})` }}
			/>)}
	</div>;
};

export const createChatSettingsProviderContext = once(<TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>>() =>
		React.createContext({} as ChatSettingsProviderContext<TUser, TChatMessage, TChat>));

export const useChatSettingsProviderContext: <TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
>(
	required?: boolean,
) => ChatSettingsProviderContext<TUser, TChatMessage, TChat> = <TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
>(required = true) => {
	const context: ChatSettingsProviderContext<TUser, TChatMessage, TChat> = React.useContext(createChatSettingsProviderContext());
	if (required && !context?.state) throw 'need ChatSettingsContext';
	return context;
};

export const chatRequests = {
	chat: 'chat',
	chatUser: 'chatUser',
	chatMessage: 'chatMessage',
	chatMessageAccess: 'chatMessageAccess',
	typing: 'typing',
	completeChat: 'chatArchive',
	removeMessage: 'deleteChatMessage',
	updateMessage: 'changeChatMessage',
	chatEmojiReactionMessage: 'chatEmojiReactionSave',
	loadChats: 'chatList',
	loadMessages: 'chatMessageList',
	getChat: 'getChat',
	getOrCreatePersonalChat: 'getOrCreatePersonalChat',
	videoChat: 'videoChat',
	readAllChatMessages: 'readAllChatMessages',
};

/**
 * ChatSettingsProvider component.
 * - need fontawesome icon styles and $width-sm variable at '@app/scss/utils/variables.scss'
 * - if error with types at some property try to add generic
 *
 * @typeParam TChatMessage - Any ChatMessage type
 * @typeParam TChat - Any Chat type
 * @param props - ChatSettingsProviderProps<TChatMessage, TChat>
 * @returns React.ReactElement
 */
export const ChatSettingsProvider = <
	TUser extends BaseUserWithAvatar = BaseUserWithAvatar,
	TChatMessage extends ChatMessage<TUser> = ChatMessage<TUser>,
	TChat extends Chat<TUser> = Chat<TUser>,
	>(props: ChatSettingsProviderProps<TUser, TChatMessage, TChat>): React.ReactElement => {
	const {
		request: defaultRequest,
		getUser: defaultGetUser,
	} = useApplicationContext();
	const {
		children,
		storageName = 'chats',
		requests = chatRequests,
		notificationTypes = {
			chat: 'Chat',
			chatUser: 'ChatUser',
			chatMessage: 'ChatMessage',
			chatReaction: 'ChatEmojiReaction',
			chatMessageAccess: 'ChatMessageAccess',
			typing: 'Typing',
			updateChatCounterNotification: 'UpdateChatCounterNotification',
			updateUserMessagesCountNotification: 'UpdateUserMessagesCountNotification',
			addChatFromArchive: 'AddChatFromArchive',
			readAllChatMessagesNotification: 'ReadAllChatMessagesNotification',
			readChatMessagesNotification: 'ReadChatMessagesNotification',
		},
		onNotificationClick,
		formSettings = {
			sendByEnter: false,
			underFormLabel: '',
			allowPasteImages: false,
			maxAttachmentsCount: 5,
			maxMessageLength: 500,
		},
		formButtons = [],
		pageSettings = {
			path: '/chats',
			chatIdUrlKey: 'chatId',
			archive: 'archive',
		},
		chatFormButtonsWrappers = {},
		otherComponents = [],
		messageActions = [],
		avatarSettings = {
			getUserAvatar: (user) => <div
				className="chat-message-list-component__item-avatar"
				style={{ backgroundImage: `url(${getAvatar(user)})` }}
			/>,
			getUserAvatarAtMention: (user) => <div
				className="chat-message-list-component__item-avatar"
				style={{ backgroundImage: `url(${getAvatar(user)})` }}
			/>,
			getChatAvatar: getUserPick,
			notificationAvatar: (state) => <img
				src={state.avatar}
				alt="avatar"
				style={{ width: '100%', height: '100%', objectFit: 'contain' }}
			/>,
		} as ChatSettingsAvatarSettings<TUser, TChat>,
		chatStoreSettings = {
			getChatStore: (state) => ({ chats: state.chats.chats, chatsLoaded: state.chats.chatsLoaded }),
			getMessages: (chatId) => (state) => state.chats.messages[chatId] || undefined,
			getCurrentChat: (state) => state.chats.currentChat,
			userUnviewedMessagesCountName: 'unviewedMessagesCount',
		} as ChatStoreSettings<TUser, TChatMessage, TChat>,
		title = 'Chats',
		formTags = [],
		renderChatName = undefined,
		messagesHeaderComponents = [],
		withRemoteId = false,
		chatsFilters: chatsFiltersProps = {},
		chatListHeaderSettings = [],
		notificationHideDelay = 5000,
		maxChatMessageNotificationCount = 5,
		removeMessageConfirmation = true,
		errorHandlers,
		emptyChatListMessage = 'There are no chats',
		basePath = '/',
		messagesLoadMoreIndicator,
		plugins = {
			[ChatPlugins.Files]: FilePlugin,
		} as any,
		listComponent = [],
		messageControlWrappers = [],
		messageControl,
		showActionsByHover = true,
		showMessagesButtonInVideoModal,
		openInModal: openInModalProps,
		openModalFromNotification = openInModalProps,
		messageAttachments,
		messageAttachmentsBefore,
		sendButtonWrapper,
		onMessageDoubleClick,
		onMessageClick,
		request = defaultRequest,
		getUser = defaultGetUser,
		handleAddChatByNewMessage,
		mobileViewWidth = 768,
	} = props;
	const isMobile = useMobileView(mobileViewWidth);
	const openInModal = !isMobile && openInModalProps;
	const openedRef = React.useRef({ value: false });

	const user = getUser();
	const ChatSettingsProviderContext = createChatSettingsProviderContext<TUser, TChatMessage, TChat>();
	const location = useLocation();
	const navigate = useNavigate();
	const locationRef = React.useRef({ location });
	const dispatch = useDispatch();
	const actions: ChatActions<TUser, TChatMessage, TChat> = React.useMemo(() => bindActionCreators(
		chatStoreSettings.getActionCreators?.()
		|| getActionCreators<TUser, ApplicationStateWithChats<TUser>, TChatMessage, TChat>(),
		dispatch,
	), []);
	const [modalMode, setModalMode] = React.useState(false);
	const [chatsFilters, setChatsFilters] = React.useState<BaseParams>(chatsFiltersProps);
	const { chatsLoaded } = useSelector(chatStoreSettings.getChatStore, shallowEqual);
	const [reloadChatId, setReloadChatId] = React.useState<number>();
	locationRef.current.location = location;

	const startChat: StartChatDelegate<TChat> = (data, requestName: string = requests?.getOrCreatePersonalChat, openChat: boolean = true) => {
		return request<TChat>(requestName, data)
			.then((result) => {
				if (chatsLoaded) {
					actions.addChat(result, storageName);
					actions.selectChat(result, storageName);
				}
				(openInModal || !openChat) && handleUrl(
					{ chatId: result.id },
					locationRef.current.location,
					navigate,
					undefined,
					'',
					true,
				);
				if (openChat) {
					if (openInModal) {
						setModalMode(() => true);
					} else {
						navigate({
							pathname: pageSettings.path,
							search: `?${pageSettings.chatIdUrlKey}=${result.id}`,
						});
						return result;
					}
				}
				handleUrl(
					{ chatId: result.id },
					locationRef.current.location,
					navigate,
					undefined,
					'',
					true,
				);
				return result;
			});
	};

	const value: {
		state: ChatSettingsProviderContextState<TUser, TChatMessage, TChat>,
		actions: ChatSettingsProviderContextActions<TChat>,
	} = React.useMemo(() => ({
		state: {
			requests,
			storageName,
			notificationTypes,
			formSettings,
			formButtons,
			chatFormButtonsWrappers,
			otherComponents,
			pageSettings,
			avatarSettings,
			chatStoreSettings: {
				getActionCreators:
					() => getActionCreators<BaseUserWithAvatar, ApplicationStateWithChats<BaseUserWithAvatar>, TChatMessage, TChat>(),
				...chatStoreSettings,
			},
			title,
			formTags,
			renderChatName,
			withRemoteId,
			chatsFilters,
			chatListHeaderSettings,
			messagesHeaderComponents,
			notificationHideDelay,
			maxChatMessageNotificationCount,
			messageActions,
			removeMessageConfirmation,
			errorHandlers,
			emptyChatListMessage,
			basePath,
			messagesLoadMoreIndicator,
			plugins: {
				...plugins,
				[ChatPlugins.Regular]: {
					...RegularMessagePlugin,
					options: plugins?.[ChatPlugins.Regular]?.options ?? RegularMessagePlugin.options,
				},
			},
			listComponent,
			messageControl,
			messageControlWrappers,
			showActionsByHover,
			showMessagesButtonInVideoModal,
			modalMode,
			openModalFromNotification,
			openInModal,
			messageAttachments,
			messageAttachmentsBefore,
			sendButtonWrapper,
			onMessageDoubleClick,
			onMessageClick,
			reloadChatId,
			getUser,
			request,
			onNotificationClick,
			handleAddChatByNewMessage,
			mobileViewWidth,
			openedRef,
		},
		actions: {
			setChatsFilters: (values) => {
				setChatsFilters((prev) => ({ ...prev, ...values }));
			},
			setModalMode,
			startChat,
			setReloadChatId,
		},
	}), [modalMode, actions, chatsLoaded, chatsFilters, reloadChatId, user, isMobile]);

	return (
		<ChatSettingsProviderContext.Provider value={value}>
			{children}
		</ChatSettingsProviderContext.Provider>
	);
};
